import { defineStore } from 'pinia';
import moment from 'moment';
import DateRangeUtil from '@/utils/dateRange';
import StatsService from '@/services/stats';

/** [start, end] pair of moment objects */
type MomentRange = any[];

interface StatsState {
  /** True while report data is being loaded */
  isLoading: boolean;
  /** Currently selected date range as [start, end] moment objects */
  dateRange: MomentRange;
  /** Human readable label for dateRange (locale aware), null until set */
  dateRangeFormatted: string | null;
  /** Comparison date range as [start, end] moments, null when comparison is off */
  dateRangeCompare: MomentRange | null;
  /** Human readable label for dateRangeCompare, null when comparison is off */
  dateRangeCompareFormatted: string | null;
  /** Selected compare preset value (e.g. previous period / previous year), null = none */
  dateCompareValue: any;
  /** Intent filter for detailed reports, null = all intents */
  intentId: string | null;
  /** Channel filter, null = all channels */
  channel: string | null;
  /** Remember if initial intents have been set */
  initialIntentsOnceSet: boolean;
  /** Intent ids selected for the comparison chart */
  selectedIntentsToCompare: string[];
  /** True while viewing the annual review report */
  isAnualReview: boolean;
  /** Year shown in the annual review */
  anualReviewYear: number;
  /** Per-month conversation data for the annual review: { sum, series }[] */
  anualConversationsPerMonth: Array<{ sum: number; series: any }>;
  /** Conversation distribution over week days for the annual review */
  anualDistributionsWeekDays: Record<string, any>;
  /** Positive feedback count for the annual review period */
  anualPosFeedback: number;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/stats.ts` (namespace `stats`).
 * In-memory only — the legacy module was NOT listed in any `vuex-persist` reducer in
 * `src/store/index.ts`, so this store gets no `vuexPersist` option.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Every legacy getter here was a pure identity passthrough of a state prop of the SAME name
 *   (`dateRange`, `dateRangeFormatted`, `dateRangeCompare`, `dateRangeCompareFormatted`,
 *   `dateCompareValue`, `intentId`, `channel`, `selectedIntentsToCompare`, `initialIntentsOnceSet`,
 *   `isAnualReview`, `anualReviewYear`, `anualConversationsPerMonth`, `anualDistributionsWeekDays`,
 *   `anualPosFeedback`, `isLoading`). Per the state-prop/getter collision rule, each is kept but
 *   renamed `<name>Getter` (consumers can also just read the state prop directly — prefer that in
 *   new code, but the renamed getter exists for mechanical parity with old `getters['stats/x']`
 *   call sites during the consumer-migration phase).
 * - No action/mutation name collisions: the sole legacy action (`loadAnualReviewData`) and the five
 *   legacy mutations (`setValue`, `setDateRange`, `setDateRangeCompare`, `setDateCompareValue`,
 *   `toggleSelectedIntentToCompare`) all have distinct names, so mutations became same-named
 *   actions with no merging needed. `loadAnualReviewData`'s internal `commit('setValue', ...)`
 *   calls became `this.setValue(...)` calls to the local action.
 */
export const useStatsStore = defineStore('stats', {
  state: (): StatsState => ({
    isLoading: false,
    dateRange: DateRangeUtil.getRangeLastXDays(27),
    dateRangeFormatted: null,
    dateRangeCompare: null,
    dateRangeCompareFormatted: null,
    dateCompareValue: null,
    intentId: null,
    channel: null,
    initialIntentsOnceSet: false, // Remember if initial intents have been set
    selectedIntentsToCompare: [],
    isAnualReview: false,
    anualReviewYear: 2025,
    anualConversationsPerMonth: [],
    anualDistributionsWeekDays: {},
    anualPosFeedback: 0,
  }),
  getters: {
    // Renamed from legacy getter `dateRange` (collides with state prop of the same name).
    dateRangeGetter: (state): MomentRange => state.dateRange,
    // Renamed from legacy getter `dateRangeFormatted` (collides with state prop of the same name).
    dateRangeFormattedGetter: (state): string | null => state.dateRangeFormatted,
    // Renamed from legacy getter `dateRangeCompare` (collides with state prop of the same name).
    dateRangeCompareGetter: (state): MomentRange | null => state.dateRangeCompare,
    // Renamed from legacy getter `dateRangeCompareFormatted` (collides with state prop of the same name).
    dateRangeCompareFormattedGetter: (state): string | null => state.dateRangeCompareFormatted,
    // Renamed from legacy getter `dateCompareValue` (collides with state prop of the same name).
    dateCompareValueGetter: (state): any => state.dateCompareValue,
    // Renamed from legacy getter `intentId` (collides with state prop of the same name).
    intentIdGetter: (state): string | null => state.intentId,
    // Renamed from legacy getter `channel` (collides with state prop of the same name).
    channelGetter: (state): string | null => state.channel,
    // Renamed from legacy getter `selectedIntentsToCompare` (collides with state prop of the same name).
    selectedIntentsToCompareGetter: (state): string[] => state.selectedIntentsToCompare,
    // Renamed from legacy getter `initialIntentsOnceSet` (collides with state prop of the same name).
    initialIntentsOnceSetGetter: (state): boolean => state.initialIntentsOnceSet,
    // Renamed from legacy getter `isAnualReview` (collides with state prop of the same name).
    isAnualReviewGetter: (state): boolean => state.isAnualReview,
    // Renamed from legacy getter `anualReviewYear` (collides with state prop of the same name).
    anualReviewYearGetter: (state): number => state.anualReviewYear,
    // Renamed from legacy getter `anualConversationsPerMonth` (collides with state prop of the same name).
    anualConversationsPerMonthGetter: (state): Array<{ sum: number; series: any }> =>
      state.anualConversationsPerMonth,
    // Renamed from legacy getter `anualDistributionsWeekDays` (collides with state prop of the same name).
    anualDistributionsWeekDaysGetter: (state): Record<string, any> => state.anualDistributionsWeekDays,
    // Renamed from legacy getter `anualPosFeedback` (collides with state prop of the same name).
    anualPosFeedbackGetter: (state): number => state.anualPosFeedback,
    // Renamed from legacy getter `isLoading` (collides with state prop of the same name).
    isLoadingGetter: (state): boolean => state.isLoading,
  },
  actions: {
    async loadAnualReviewData({
      botId,
      channel,
      year,
    }: {
      botId: string;
      channel: string | null;
      year: number;
    }) {
      this.setValue({
        key: 'anualReviewYear',
        value: year,
      });

      // 1. Conversations per month
      let monthIndex = 0;
      const conversationsPerMonth = [];

      while (monthIndex < 12) {
        const startOfMonth = moment()
          .year(year)
          .month(monthIndex)
          .startOf('month');

        const endOfMonth = moment()
          .year(year)
          .month(monthIndex)
          .endOf('month');

        const dateRange = [startOfMonth, endOfMonth];

        const { sum, series }: any = await StatsService.getConversationSeries(
          botId,
          dateRange,
          channel
        );

        conversationsPerMonth.push({ sum, series });

        monthIndex++;
      }

      this.setValue({
        key: 'anualConversationsPerMonth',
        value: conversationsPerMonth,
      });

      // 2. Distributions Week Days
      const dateRange = DateRangeUtil.getAnualReviewDateRange(year);
      const distributionsWeekDays = await StatsService.getConversationDistribution(
        botId,
        dateRange,
        channel
      );
      this.setValue({
        key: 'anualDistributionsWeekDays',
        value: distributionsWeekDays,
      });

      // 3. Positive Feedback for anual review
      const { posCount }: any = await StatsService.getFeedbackSeries(
        botId,
        dateRange,
        channel
      );

      this.setValue({
        key: 'anualPosFeedback',
        value: posCount,
      });
    },
    /** Converted from legacy mutation `setValue`. */
    setValue({ key, value }: { key: keyof StatsState; value: any }) {
      (this as any)[key] = value;
    },
    /** Converted from legacy mutation `setDateRange`. */
    setDateRange({ dateRange, language }: { dateRange: any[]; language?: string }) {
      this.dateRange = dateRange.map((date) => moment(date));
      // Cast: legacy passed the raw (pre-moment) array here too, relying on Vuex's untyped `any` context.
      this.dateRangeFormatted = DateRangeUtil.getDateRangeFormatted({
        dateRange: dateRange as any,
        language,
      });
    },
    /** Converted from legacy mutation `setDateRangeCompare`. */
    setDateRangeCompare({
      dateRange,
      language,
    }: {
      dateRange: any[] | null;
      language?: string;
    }) {
      if (!dateRange) {
        this.dateRangeCompare = null;
        this.dateRangeCompareFormatted = null;
        return;
      }
      this.dateRangeCompare = dateRange.map((date) => moment(date));
      // Cast: legacy passed the raw (pre-moment) array here too, relying on Vuex's untyped `any` context.
      this.dateRangeCompareFormatted = DateRangeUtil.getDateRangeFormatted({
        dateRange: dateRange as any,
        language,
      });
    },
    /** Converted from legacy mutation `setDateCompareValue`. */
    setDateCompareValue(dateCompareValue: any) {
      this.dateCompareValue = dateCompareValue;
    },
    /** Converted from legacy mutation `toggleSelectedIntentToCompare`. */
    toggleSelectedIntentToCompare(intentId: string) {
      if (this.selectedIntentsToCompare.includes(intentId)) {
        this.selectedIntentsToCompare = this.selectedIntentsToCompare.filter(
          (i) => i !== intentId
        );
      } else {
        this.selectedIntentsToCompare.push(intentId);
      }
    },
  },
});
